"use client";

import React, { useEffect, useRef, useState } from "react";

interface PricingTier {
  id: string;
  name: string;
  price: { monthly: number | string; annual: number | string };
  period: string;
  description: string;
  highlight?: string;
  highlightColor?: string;
  cta: string;
  ctaStyle: "primary" | "secondary" | "outline";
  badge?: string;
  features: Array<{ text: string; included: boolean; note?: string }>;
  popular?: boolean;
}

const tiers: PricingTier[] = [
  {
    id: "foundry",
    name: "Foundry",
    price: { monthly: 0, annual: 0 },
    period: "forever",
    description: "For individuals who want a taste of AI-powered email clarity.",
    cta: "Start Free",
    ctaStyle: "outline",
    features: [
      { text: "1 email account (Gmail or Outlook)", included: true },
      { text: "Elevate Brief (3x per week)", included: true },
      { text: "AI Triage — top 10 emails flagged", included: true },
      { text: "Basic Ghostwriter (5 drafts/week)", included: true },
      { text: "Airlock Scheduling Engine", included: false },
      { text: "Long-Term Relationship Memory", included: false },
      { text: "Multi-account support", included: false },
      { text: "Haptic UX & mobile push", included: false },
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: { monthly: 22, annual: 18 },
    period: "/month",
    description: "The full Triple-Threat for high-output individual executives.",
    cta: "Start Pro Trial",
    ctaStyle: "primary",
    popular: true,
    badge: "Most Popular",
    features: [
      { text: "2 email accounts (Gmail + Outlook)", included: true },
      { text: "Elevate Brief — daily at 6 AM", included: true },
      { text: "Full AI Triage — unlimited", included: true },
      { text: "Ghostwriter — unlimited drafts", included: true },
      { text: "Airlock Scheduling Engine", included: true },
      { text: "Long-Term Relationship Memory", included: true },
      { text: "Multi-account unified inbox", included: true },
      { text: "Haptic UX & mobile push", included: true },
    ],
  },
  {
    id: "team",
    name: "Team",
    price: { monthly: 18, annual: 15 },
    period: "/user/month",
    description: "Roll out ElevateEmail across your executive team.",
    cta: "Start Team Trial",
    ctaStyle: "secondary",
    highlight: "Min 3 users",
    highlightColor: "#818CF8",
    features: [
      { text: "Everything in Pro", included: true },
      { text: "Cross-team calendar coordination", included: true },
      { text: "Shared Airlock rule templates", included: true },
      { text: "Team stress heatmap dashboard", included: true },
      { text: "Admin controls & audit log", included: true },
      { text: "Priority onboarding support", included: true },
      { text: "SSO (SAML 2.0)", included: false },
      { text: "Custom AI tone profiles", included: false },
    ],
  },
  {
    id: "sovereign",
    name: "Sovereign",
    price: { monthly: 45, annual: 38 },
    period: "/month",
    description: "For C-suite principals who need white-glove AI management.",
    cta: "Talk to Sales",
    ctaStyle: "secondary",
    badge: "C-Suite",
    features: [
      { text: "Everything in Team", included: true },
      { text: "Unlimited email accounts", included: true },
      { text: "Custom AI persona training", included: true },
      { text: "Dedicated success manager", included: true },
      { text: "SSO (SAML 2.0) + SCIM", included: true },
      { text: "Custom tone & brand voice", included: true },
      { text: "Priority 1-hour SLA support", included: true },
      { text: "Executive concierge onboarding", included: true },
    ],
  },
];

const CheckIcon: React.FC<{ color?: string }> = ({ color = "#22C55E" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <path d="M20 6L9 17l-5-5" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

const XIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="shrink-0">
    <path d="M18 6L6 18M6 6l12 12" stroke="#3A3A3A" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const PricingSection: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const getCtaStyles = (style: PricingTier["ctaStyle"], popular?: boolean) => {
    if (style === "primary" || popular) {
      return {
        background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
        color: "#0D0F14",
        border: "none",
      };
    }
    if (style === "secondary") {
      return {
        background: "rgba(255,255,255,0.06)",
        color: "#F0EDE8",
        border: "1px solid rgba(255,255,255,0.12)",
      };
    }
    return {
      background: "transparent",
      color: "#9B9891",
      border: "1px solid rgba(255,255,255,0.1)",
    };
  };

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="relative py-32 px-6 overflow-hidden"
      style={{ background: "#0D0F14" }}
    >
      {/* Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(245,158,11,0.07) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section divider */}
        <div className="section-divider mb-20" />

        {/* Header */}
        <div
          className="text-center mb-14"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(24px)",
            transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 text-xs font-semibold tracking-widest uppercase"
            style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.15)", color: "#F59E0B" }}
          >
            Simple Pricing
          </div>
          <h2
            className="font-display leading-tight mb-4"
            style={{ fontSize: "clamp(36px, 5vw, 60px)", color: "#F0EDE8", letterSpacing: "-0.02em" }}
          >
            Start free.{" "}
            <span className="font-display italic" style={{ color: "#9B9891" }}>Scale when ready.</span>
          </h2>
          <p className="max-w-lg mx-auto mb-10" style={{ color: "#9B9891", fontSize: "18px", lineHeight: "1.6" }}>
            All plans include a 14-day trial of Pro features. No credit card required to start.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-4 p-1.5 rounded-2xl" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <button
              onClick={() => setAnnual(false)}
              className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300"
              style={{
                background: !annual ? "rgba(255,255,255,0.1)" : "transparent",
                color: !annual ? "#F0EDE8" : "#5C5A56",
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-300"
              style={{
                background: annual ? "rgba(245,158,11,0.15)" : "transparent",
                color: annual ? "#F59E0B" : "#5C5A56",
              }}
            >
              Annual
              <span
                className="text-xs px-2 py-0.5 rounded-full font-bold"
                style={{ background: "rgba(245,158,11,0.2)", color: "#F59E0B", fontSize: "10px" }}
              >
                Save 18%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {tiers.map((tier, idx) => {
            const price = annual ? tier.price.annual : tier.price.monthly;
            const isPopular = tier.popular;

            return (
              <div
                key={tier.id}
                className="relative rounded-3xl p-6 flex flex-col transition-all duration-500"
                style={{
                  background: isPopular
                    ? "linear-gradient(145deg, rgba(245,158,11,0.08) 0%, #1A1D26 40%, #13151C 100%)"
                    : "linear-gradient(145deg, #1A1D26 0%, #13151C 100%)",
                  border: `1px solid ${isPopular ? "rgba(245,158,11,0.3)" : "rgba(255,255,255,0.06)"}`,
                  boxShadow: isPopular ? "0 0 60px rgba(245,158,11,0.08)" : "none",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(32px)",
                  transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${idx * 0.1}s`,
                }}
              >
                {/* Popular badge */}
                {tier.badge && (
                  <div
                    className="absolute -top-3 left-1/2 text-xs font-bold px-4 py-1.5 rounded-full"
                    style={{
                      transform: "translateX(-50%)",
                      background: isPopular
                        ? "linear-gradient(135deg, #F59E0B, #D97706)"
                        : "rgba(129,140,248,0.2)",
                      color: isPopular ? "#0D0F14" : "#818CF8",
                      border: isPopular ? "none" : "1px solid rgba(129,140,248,0.3)",
                    }}
                  >
                    {tier.badge}
                  </div>
                )}

                {/* Tier header */}
                <div className="mb-6 pt-2">
                  <div className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: isPopular ? "#F59E0B" : "#5C5A56" }}>
                    {tier.name}
                  </div>
                  <div className="flex items-end gap-1 mb-1">
                    {typeof price === "number" ? (
                      <>
                        <span className="font-display font-semibold" style={{ fontSize: "42px", color: "#F0EDE8", lineHeight: 1, letterSpacing: "-0.03em" }}>
                          {price === 0 ? "Free" : `$${price}`}
                        </span>
                        {price !== 0 && (
                          <span className="text-sm pb-2" style={{ color: "#5C5A56" }}>{tier.period}</span>
                        )}
                      </>
                    ) : (
                      <span className="font-display font-semibold" style={{ fontSize: "32px", color: "#F0EDE8", letterSpacing: "-0.02em" }}>
                        {price}
                      </span>
                    )}
                  </div>
                  {tier.highlight && (
                    <span className="text-xs" style={{ color: tier.highlightColor }}>
                      {tier.highlight}
                    </span>
                  )}
                  <p className="text-xs leading-relaxed mt-3" style={{ color: "#5C5A56" }}>
                    {tier.description}
                  </p>
                </div>

                {/* CTA */}
                <button
                  className="w-full py-3 rounded-xl text-sm font-semibold mb-6 transition-all duration-300"
                  style={getCtaStyles(tier.ctaStyle, isPopular)}
                  onMouseEnter={(e) => {
                    if (isPopular || tier.ctaStyle === "primary") {
                      (e.currentTarget as HTMLElement).style.opacity = "0.9";
                      (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.opacity = "1";
                    (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                  }}
                >
                  {tier.cta}
                </button>

                {/* Feature divider */}
                <div className="section-divider mb-5" />

                {/* Features */}
                <ul className="space-y-3 flex-1">
                  {tier.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2.5">
                      {f.included ? <CheckIcon color={isPopular ? "#F59E0B" : "#22C55E"} /> : <XIcon />}
                      <span
                        className="text-xs leading-relaxed"
                        style={{ color: f.included ? "#9B9891" : "#3A3A3A" }}
                      >
                        {f.text}
                        {f.note && (
                          <span className="ml-1 px-1.5 py-0.5 rounded text-xs" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B", fontSize: "10px" }}>
                            {f.note}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <p
          className="text-center text-sm mt-10"
          style={{
            color: "#5C5A56",
            opacity: visible ? 1 : 0,
            transition: "opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.6s",
          }}
        >
          All plans include 256-bit encryption, GDPR compliance, and 99.9% uptime SLA.{" "}
          <a href="#" style={{ color: "#F59E0B", textDecoration: "underline", textUnderlineOffset: "3px" }}>
            Compare all features →
          </a>
        </p>
      </div>
    </section>
  );
};

export default PricingSection;